﻿@file:OptIn(ExperimentalComposeWebApi::class)

package org.example.project.dashboard

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.enginechat.ChatPopupOverlayLayer
import org.example.project.enginechat.CRChatPopupOverlayLayer
import org.example.project.enginechat.KontaktKalenderOverlayLayer
import org.example.project.engineSnap.SnapPopupOverlayLayer
import org.example.project.enginesmartring.ENGINE_SMARTRING_SCREEN_KEY
import org.example.project.enginesmartring.EngineSmartRingHost
import org.example.project.screens_cus.LhmTypCreateDialogOverlayLayer
import org.example.project.screens_cus.SnapverwaltungCreateDialogOverlayLayer
import org.example.project.screens_shared.NotepadOverlayLayer
import org.example.project.service.FeedControlApi
import org.example.project.service.FrameworkUserSettingsService
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.LoginUserApi
import org.example.project.service.rememberActiveBuildName
import org.example.project.enginetabular.TableStyles
import org.example.project.settings.LogoSettings
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event
import kotlin.math.ceil
import kotlin.math.floor

object DashboardTheme {
    val SIDEBAR_WIDTH_FULL = 170.px
    val BOTTOM_BAR_HEIGHT = 40.px
    val TOP_BUTTON_BAR_HEIGHT = 60.px
    val BACKGROUND_APP = Color("#17191d")
    const val BACKGROUND_APP_GRADIENT =
        "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 28%, rgba(255,255,255,0.018) 62%, rgba(8,10,14,1) 100%), linear-gradient(135deg, rgba(28,32,39,1) 0%, rgba(16,19,24,1) 46%, rgba(8,10,14,1) 100%)"
    // Independent backgrounds; the general surface gradient remains unchanged.
    const val DASHBOARD_BG = "#070707"
    const val SIDEBAR_BG = "#151518"
    const val POPUP_BG =
        "linear-gradient(160deg, rgba(34,38,45,0.98) 0%, rgba(18,21,26,0.99) 46%, rgba(8,10,14,1) 100%)"
    const val PREMIUM_SURFACE_BG =
        "linear-gradient(160deg, rgba(34,38,45,0.98) 0%, rgba(18,21,26,0.99) 46%, rgba(8,10,14,1) 100%)"
    const val TOPBAR_SURFACE_BG =
        "linear-gradient(180deg, rgba(52, 58, 68, 0.95) 0%, rgba(31, 35, 42, 0.98) 100%)"
    val ACCENT = UISettings.ACCENT
    val TEXT_LIGHT = Color("#F7F7F5")
    val TEXT_MUTED = Color("rgba(247, 247, 245, 0.72)")
    val BORDER_LIGHT = Color("rgba(255, 255, 255, 0.10)")
    val SURFACE_SHADOW = Color("rgba(0, 0, 0, 0.38)")
    // Sehr subtile Card-Fläche auf dem Gradient — genau so viel Tiefe wie nötig
    val SURFACE_CARD = Color("rgba(255, 255, 255, 0.03)")
    // Tiefstes Panel — für Login-Cards und Standalone-Screen-Panels
    val SURFACE_DEEP = Color("#0b0c0e")
    // Hellblau für selektierte Listen-Zeilen (z. B. RollenVerwaltung aktive Zeile)

    const val ICON_FILTER_WHITE = "brightness(0) invert(1)"
    const val ICON_FILTER_ACTIVE = UISettings.ICON_FILTER_ACCENT
    val ICON_FILTER_ACTIVE_GLOW =
        "${UISettings.ICON_FILTER_ACCENT} drop-shadow(0 0 8px ${UISettings.accentRgba(0.72)})"
    const val NAVIGATION_LAYER_Z_INDEX = 30000
    const val NAVIGATION_POPUP_Z_INDEX = 30010

    // Semantische Status-Farben
    val STATUS_BELEGT = Color("#e53935")   // rot  – besetzt / blockiert
    val STATUS_FREI   = Color("#43a047")   // grün – frei / verfügbar
}

object ContentOffset {
    val LEFT = 190.px
    val RIGHT = 20.px
    val TOP = 90.px
    val BOTTOM = 56.px
}

private const val QUICK_NAV_ITEM_ESTIMATED_WIDTH = 180
private const val QUICK_NAV_SIDE_RESERVE = 180
private const val DASHBOARD_FOOTER_LEFT_RESERVE = 440
private const val DASHBOARD_FOOTER_RIGHT_RESERVE = 440
private const val DASHBOARD_SIDEBAR_COLLAPSED_WIDTH_PX = 64

private fun isNotificationHubAvailable(art: String): Boolean =
    art == DashboardNode.SYS || art == DashboardNode.CUS || art == DashboardNode.NET

private fun dashboardCompanyLabel(firmenId: String, company: All?): String {
    if (firmenId.isBlank()) return "FEHLER: FirmenId fehlt"
    if (company == null) return "FEHLER: Firma fehlt"
    return company.kunde.trim().ifBlank {
        company.firma.trim().ifBlank { "FEHLER: Firma fehlt" }
    }
}

private fun sortDashboardScreensAlphabetically(screens: List<All>): List<All> =
    screens.sortedWith { left, right ->
        left.label.ifBlank { left.key }.asDynamic().localeCompare(
            right.label.ifBlank { right.key },
            "de",
            js("{ sensitivity: 'base' }")
        ) as Int
    }

private fun completeDashboardFeedVisibility(settings: Map<String, Boolean>): Map<String, Boolean> =
    DashboardFeedRegistry.definitions.associate { definition ->
        definition.key to (settings[definition.key] ?: true)
    }

private fun dashboardLayoutUserLabel(user: All?): String {
    if (user == null) return "User"
    val firstInitial = user.vorname.firstOrNull { it.isLetter() }?.uppercaseChar()?.toString().orEmpty()
    val lastName = user.nachname
        .split(Regex("\\s+"))
        .filter { it.isNotBlank() }
        .joinToString(" ") { part ->
            val lower = part.lowercase()
            lower.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
        }
    return when {
        firstInitial.isBlank() && lastName.isBlank() -> user.id.ifBlank { "User" }
        firstInitial.isBlank() -> lastName
        lastName.isBlank() -> firstInitial
        else -> "$firstInitial.$lastName"
    }
}

private fun dashboardLayoutUserFullName(user: All?): String {
    if (user == null) return "User"
    return listOf(user.vorname.trim(), user.nachname.trim())
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .ifBlank { user.name.ifBlank { user.id.ifBlank { "User" } } }
}

private fun estimateDashboardSidebarWidthPx(
    categories: Map<String, List<All>>,
    sidebarScreens: List<All>,
    sidebarMode: String
): Int {
    fun textWidthPx(text: String): Double =
        text.sumOf { char ->
            when {
                char.isWhitespace() -> 4.0
                char.isUpperCase() -> 8.2
                char in "ilI.,:;|!" -> 3.8
                char in "mwMW" -> 10.2
                else -> 7.2
            }
        }

    val logoWidth = LogoSettings.desktopDashboardSidebarLogoWidthPx + 20.0
    val categoryWidth = categories.keys.maxOfOrNull { textWidthPx(it) + 74.0 } ?: 0.0
    val categorizedScreenWidth = categories.values.flatten()
        .maxOfOrNull { textWidthPx(it.label.ifBlank { it.key }) + 84.0 } ?: 0.0
    val flatScreenWidth = sidebarScreens
        .maxOfOrNull { textWidthPx(it.label.ifBlank { it.key }) + 104.0 } ?: 0.0
    val contentWidth = if (sidebarMode == SidebarMode.CATEGORIZED) {
        maxOf(categoryWidth, categorizedScreenWidth)
    } else {
        flatScreenWidth
    }

    return ceil(maxOf(logoWidth, contentWidth, 170.0)).toInt().coerceAtMost(320)
}

@Composable
fun DashboardLayout(
    art: String,
    topBarScreens: List<All>,
    categories: Map<String, List<All>>,
    categoryIcons: Map<String, String>,
    sidebarScreens: List<All>,
    sidebarMode: String,
    selectedKey: String,
    selectedNavigationKey: String = selectedKey,
    selectedInstanceKey: String = selectedKey,
    forceContentScreenKey: String? = null,
    contentOnlyMode: Boolean = false,
    onSelect: (String) -> Unit,
    onHome: () -> Unit,
    onLogout: () -> Unit,
    extraScreens: List<All> = emptyList(),
    profilePopupKey: String? = null,
    onOpenProfile: (() -> Unit)? = null
) {
    val scope = rememberCoroutineScope()
    var pinnedScreens by remember { mutableStateOf<List<String>>(emptyList()) }
    var quickNavOffset by remember { mutableStateOf(0) }
    var viewportWidth by remember { mutableStateOf(window.innerWidth.toDouble()) }
    var openTopbarScreens by remember { mutableStateOf<List<String>>(emptyList()) }
    var topbarAutoHide by remember { mutableStateOf(false) }
    var sidebarAutoHide by remember { mutableStateOf(false) }
    var sidebarCollapsed by remember { mutableStateOf(false) }
    var monitorEnabled by remember { mutableStateOf(true) }
    var userFeedPushEnabled by remember { mutableStateOf(DashboardFeedRegistry.defaultConfig().keys.associateWith { true }) }
    var isTopbarVisible by remember { mutableStateOf(true) }
    var isSidebarVisible by remember { mutableStateOf(true) }
    var currentLoginUser by remember { mutableStateOf(LoginService.getUser()) }
    var currentCompany by remember { mutableStateOf<All?>(null) }
    var dashboardMonitor by remember(art) { mutableStateOf(emptyDashboardMonitor()) }
    var dashboardFeedConfig by remember { mutableStateOf(DashboardFeedRegistry.defaultConfig()) }
    var pendingFeedSettingsSave by remember { mutableStateOf<Map<String, Boolean>?>(null) }
    val sessionConfigVersion = GlobalManager.sessionConfigVersion()
    val userSettingsVersion = FrameworkUserSettingsService.currentUserSettingsVersion()
    val snapRequestVersion = GlobalManager.jsonTopicVersion("request.json")
    val monitorAvailable = isNotificationHubAvailable(art)
    val layoutSettingsKey = remember(art) { dashboardLayoutSettingsKey(art) }
    var layoutPreferencesReady by remember(layoutSettingsKey) { mutableStateOf(contentOnlyMode) }
    val activeBuildName = rememberActiveBuildName()

    val resolvedTopBarScreens = remember(topBarScreens) {
        sortDashboardScreensAlphabetically(topBarScreens)
    }
    val resolvedCategories = remember(categories) {
        categories.mapValues { (_, screens) ->
            sortDashboardScreensAlphabetically(screens)
        }
    }
    val resolvedSidebarScreens = remember(sidebarScreens) { sidebarScreens }
    val allScreens = remember(resolvedCategories, resolvedSidebarScreens, resolvedTopBarScreens, extraScreens) {
        (resolvedCategories.values.flatten() + resolvedSidebarScreens + resolvedTopBarScreens + extraScreens)
            .distinctBy { it.key }
    }
    val topbarScreensOrdered = remember(resolvedTopBarScreens) { resolvedTopBarScreens }
    val activeContentLabel = remember(art, selectedKey, allScreens, currentLoginUser) {
        when {
            selectedKey == "MeinProfilScreen" -> dashboardLayoutUserFullName(currentLoginUser)
            art == DashboardNode.NET && selectedKey.isBlank() -> "Home"
            else -> allScreens.firstOrNull { it.key == selectedKey }?.label?.takeIf { it.isNotBlank() } ?: "Home"
        }
    }
    val activeFirmenId = remember(currentLoginUser, sessionConfigVersion) {
        currentLoginUser?.firmenId.orEmpty().trim().ifBlank { GlobalManager.currentFirmenId.trim() }
    }
    val activeCompanyLabel = remember(activeFirmenId, currentCompany) {
        dashboardCompanyLabel(activeFirmenId, currentCompany)
    }
    val companyIdentityScreenKey = remember(art) {
        when (art) {
            DashboardNode.SYS -> "Unternehmenstamm"
            DashboardNode.NET -> "NetStandortVerwaltung"
            else -> "StandortVerwaltung"
        }
    }
    val feedDefinitionsWithStatus = remember(art, dashboardFeedConfig) {
        DashboardFeedRegistry.definitions.map { definition ->
            definition to FeedControlApi.isFeedEnabled(dashboardFeedConfig, definition.key, art)
        }
    }
    val startScreenOptions = remember(allScreens) {
        allScreens
            .filter { it.key.isNotBlank() }
            .filter { it.screenMode != ScreenMode.POPUP }
            .distinctBy { it.key }
            .sortedBy { it.label.ifBlank { it.key }.lowercase() }
            .map { screen -> All(key = screen.key, label = screen.label.ifBlank { screen.key }) }
    }
    val sidebarWidthPx = remember(resolvedCategories, resolvedSidebarScreens, sidebarMode) {
        estimateDashboardSidebarWidthPx(
            categories = resolvedCategories,
            sidebarScreens = resolvedSidebarScreens,
            sidebarMode = sidebarMode
        )
    }
    val visibleSidebarWidthPx = if (sidebarCollapsed) DASHBOARD_SIDEBAR_COLLAPSED_WIDTH_PX else sidebarWidthPx
    val contentLeftOffset = if (sidebarAutoHide && !isSidebarVisible) {
        ContentOffset.RIGHT
    } else {
        (visibleSidebarWidthPx + 20).px
    }
    val topControlsLeftOffsetPx = if (sidebarAutoHide && !isSidebarVisible) {
        50.0
    } else {
        visibleSidebarWidthPx + 50.0
    }
    val contentTopOffset = if (contentOnlyMode) 0.px else 70.px
    val contentBottomOffset = if (contentOnlyMode) 0.px else ContentOffset.BOTTOM
    val popupTopOffset = contentTopOffset
    val quickNavVisibleCount = floor(
        ((viewportWidth - QUICK_NAV_SIDE_RESERVE - DASHBOARD_FOOTER_LEFT_RESERVE - DASHBOARD_FOOTER_RIGHT_RESERVE) / QUICK_NAV_ITEM_ESTIMATED_WIDTH.toDouble())
            .coerceAtLeast(1.0)
    ).toInt().coerceAtLeast(1)
    val quickNavMaxOffset = (pinnedScreens.size - quickNavVisibleCount).coerceAtLeast(0)
    val visiblePinnedScreens = pinnedScreens.drop(quickNavOffset).take(quickNavVisibleCount)
    val retainedContentKeys = remember(selectedKey, pinnedScreens) {
        (pinnedScreens + selectedKey)
            .filter { it.isNotBlank() }
            .distinct()
    }

    Style(DashboardStyles)
    Style(TableStyles)

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = {
            viewportWidth = window.innerWidth.toDouble()
        }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    LaunchedEffect(pinnedScreens.size, quickNavVisibleCount) {
        quickNavOffset = quickNavOffset.coerceIn(0, quickNavMaxOffset)
    }

    LaunchedEffect(selectedKey, pinnedScreens, quickNavVisibleCount) {
        val selectedIndex = pinnedScreens.indexOf(selectedKey)
        if (selectedIndex >= 0) {
            quickNavOffset = when {
                selectedIndex < quickNavOffset -> selectedIndex
                selectedIndex >= quickNavOffset + quickNavVisibleCount -> selectedIndex - quickNavVisibleCount + 1
                else -> quickNavOffset
            }.coerceIn(0, quickNavMaxOffset)
        }
    }

    fun persistLayoutSettings(
        nextTopbarAutoHide: Boolean = topbarAutoHide,
        nextSidebarAutoHide: Boolean = sidebarAutoHide,
        nextSidebarCollapsed: Boolean = sidebarCollapsed,
        nextMonitorEnabled: Boolean = monitorEnabled,
        nextUserFeedPushEnabled: Map<String, Boolean> = userFeedPushEnabled
    ) {
        scope.launch {
            saveDashboardLayoutPreferences(
                settingsKey = layoutSettingsKey,
                preferences = DashboardLayoutPreferences(
                    topbarAutoHide = nextTopbarAutoHide,
                    sidebarAutoHide = nextSidebarAutoHide,
                    sidebarCollapsed = nextSidebarCollapsed,
                    monitorEnabled = nextMonitorEnabled,
                    feedPushEnabled = nextUserFeedPushEnabled
                )
            )
        }
    }

    fun saveFeedPushSetting(key: String, enabled: Boolean) {
        val next = userFeedPushEnabled + (key to enabled)
        userFeedPushEnabled = next
        persistLayoutSettings(nextUserFeedPushEnabled = next)
    }

    fun saveFeedSettings(next: Map<String, Boolean>) {
        pendingFeedSettingsSave = next
    }

    fun saveStartScreen(nextStartScreen: String) {
        scope.launch {
            LoginUserApi.saveCurrentLoginUser { user ->
                user.copy(startbildschirm = nextStartScreen)
            }?.let { saved ->
                LoginService.updateUser { saved }
                currentLoginUser = saved
                GlobalManager.triggerSessionConfigReload()
            }
        }
    }

    LaunchedEffect(pendingFeedSettingsSave) {
        val nextFeedSettings = pendingFeedSettingsSave ?: return@LaunchedEffect
        saveDashboardLayoutPreferences(
            settingsKey = layoutSettingsKey,
            preferences = DashboardLayoutPreferences(
                topbarAutoHide = topbarAutoHide,
                sidebarAutoHide = sidebarAutoHide,
                sidebarCollapsed = sidebarCollapsed,
                monitorEnabled = monitorEnabled,
                feedPushEnabled = nextFeedSettings
            )
        )
        userFeedPushEnabled = nextFeedSettings
        pendingFeedSettingsSave = null
        GlobalManager.triggerSessionConfigReload()
    }

    DashboardRuntimeController(
        art = art,
        selectedKey = selectedKey,
        layoutSettingsKey = layoutSettingsKey,
        sessionConfigVersion = sessionConfigVersion,
        userSettingsVersion = userSettingsVersion,
        snapRequestVersion = snapRequestVersion,
        monitorAvailable = monitorAvailable,
        monitorEnabled = monitorEnabled,
        activeFirmenId = activeFirmenId,
        forceContentScreenKey = forceContentScreenKey,
        currentLoginUserId = currentLoginUser?.id,
        openTopbarScreens = openTopbarScreens,
        onCurrentLoginUserChange = { currentLoginUser = it },
        onCurrentCompanyChange = { currentCompany = it },
        onTopbarAutoHideChange = { topbarAutoHide = it },
        onSidebarAutoHideChange = { sidebarAutoHide = it },
        onSidebarCollapsedChange = { sidebarCollapsed = it },
        onMonitorEnabledPreferenceChange = { monitorEnabled = it },
        onUserFeedPushEnabledChange = { userFeedPushEnabled = completeDashboardFeedVisibility(it) },
        onTopbarVisibilityChange = { isTopbarVisible = it },
        onSidebarVisibilityChange = { isSidebarVisible = it },
        onDashboardFeedConfigChange = { dashboardFeedConfig = it },
        onDashboardMonitorChange = { dashboardMonitor = it },
        onOpenTopbarScreensChange = { openTopbarScreens = it },
        onLayoutPreferencesReady = { layoutPreferencesReady = true },
        userFeedVisible = userFeedPushEnabled
    )

    fun openMonitorTarget(key: String) {
        if (key == "ChatModuleScreen") {
            if (key !in openTopbarScreens) {
                openTopbarScreens = openTopbarScreens + key
            }
            return
        }
        onSelect(key)
    }

    fun openNavigationTarget(key: String) {
        val screen = allScreens.firstOrNull { it.key == key } ?: return
        if (screen.screenMode == ScreenMode.POPUP) {
            openTopbarScreens = if (key in openTopbarScreens) {
                openTopbarScreens.filterNot { it == key }
            } else {
                openTopbarScreens + key
            }
        } else {
            onSelect(key)
            if (key !in pinnedScreens) {
                pinnedScreens = pinnedScreens + key
            }
        }
    }

    DashboardProfileSettingsBridge.update(
        feedDefinitionsWithStatus = feedDefinitionsWithStatus,
        userFeedPushEnabled = userFeedPushEnabled,
        startScreenOptions = startScreenOptions,
        selectedStartScreen = currentLoginUser?.startbildschirm.orEmpty(),
        onFeedPushEnabledChange = ::saveFeedPushSetting,
        onFeedSettingsSave = ::saveFeedSettings,
        onStartScreenSave = ::saveStartScreen
    )

    Div({ classes(DashboardStyles.appRoot) }) {
        if (sidebarAutoHide && !isSidebarVisible) {
            Div({
                classes(DashboardStyles.sidebarHotzone)
                onMouseEnter { isSidebarVisible = true }
            })
        }

        DashboardSidebar(
            categories = resolvedCategories,
            categoryIcons = categoryIcons,
            sidebarScreens = resolvedSidebarScreens,
            popupScreens = topbarScreensOrdered,
            openTopbarScreens = openTopbarScreens,
            sidebarMode = sidebarMode,
            sidebarWidthPx = visibleSidebarWidthPx,
            expandedSidebarWidthPx = sidebarWidthPx,
            sidebarCollapsed = sidebarCollapsed,
            sidebarAutoHide = sidebarAutoHide,
            isSidebarVisible = isSidebarVisible,
            selectedKey = selectedNavigationKey,
            activeCompanyLabel = activeCompanyLabel,
            currentUser = dashboardLayoutUserLabel(currentLoginUser),
            onSidebarCollapsedChange = { next ->
                sidebarCollapsed = next
                persistLayoutSettings(nextSidebarCollapsed = next)
            },
            monitorAvailable = monitorAvailable,
            monitorEnabled = monitorEnabled,
            feedDefinitionsWithStatus = feedDefinitionsWithStatus,
            userFeedPushEnabled = userFeedPushEnabled,
            onSidebarAutoHideChange = { next ->
                sidebarAutoHide = next
                persistLayoutSettings(nextSidebarAutoHide = next)
            },
            topbarAutoHide = topbarAutoHide,
            onTopbarAutoHideChange = { next ->
                topbarAutoHide = next
                persistLayoutSettings(nextTopbarAutoHide = next)
            },
            onMonitorEnabledChange = { next ->
                monitorEnabled = next
                persistLayoutSettings(nextMonitorEnabled = next)
            },
            onFeedPushEnabledChange = ::saveFeedPushSetting,
            onToggleTopbarScreen = { key, isOpen ->
                openTopbarScreens = if (isOpen) {
                    openTopbarScreens.filterNot { it == key }
                } else {
                    openTopbarScreens + key
                }
            },
            onSelect = ::openNavigationTarget,
            onHome = onHome,
            onLogout = onLogout,
            onOpenCompany = { openNavigationTarget(companyIdentityScreenKey) },
            onOpenProfile = onOpenProfile,
            onOpenProfilePopup = profilePopupKey?.let { key -> { openNavigationTarget(key) } },
            onPin = {},
            onMouseLeave = { if (sidebarAutoHide) isSidebarVisible = false }
        )

        if (!contentOnlyMode) {
            DashboardFooterControls(
                popupScreens = emptyList(),
                openTopbarScreens = openTopbarScreens,
                topLeftOffsetPx = topControlsLeftOffsetPx,
                activeCompanyLabel = activeCompanyLabel,
                currentUser = dashboardLayoutUserLabel(currentLoginUser),
                monitorAvailable = monitorAvailable,
                monitorEnabled = monitorEnabled,
                dashboardMonitor = dashboardMonitor,
                userFeedPushEnabled = userFeedPushEnabled,
                feedDefinitionsWithStatus = feedDefinitionsWithStatus,
                startScreenOptions = startScreenOptions,
                selectedStartScreen = currentLoginUser?.startbildschirm.orEmpty(),
                onOpenMonitorTarget = ::openMonitorTarget,
                onToggleTopbarScreen = { key, isOpen ->
                    openTopbarScreens = if (isOpen) {
                        openTopbarScreens.filterNot { it == key }
                    } else {
                        openTopbarScreens + key
                    }
                },
                onFeedPushEnabledChange = ::saveFeedPushSetting,
                onFeedSettingsSave = ::saveFeedSettings,
                onStartScreenSave = ::saveStartScreen,
                onPasswordChange = onOpenProfile,
                onLogout = onLogout
            )
        }

        if (!contentOnlyMode && activeBuildName.isNotBlank()) {
            val isDevelopmentMode = activeBuildName == "Entwicklermodus"
            val dashboardVersionText = when {
                isDevelopmentMode -> "ENTWICKLERMODUS"
                else -> activeBuildName
            }
            Div({
                style {
                    position(Position.Fixed)
                    right(12.px)
                    bottom(4.px)
                    color(
                        when {
                            isDevelopmentMode -> Color("#ff9f1c")
                            else -> DashboardTheme.TEXT_MUTED
                        }
                    )
                    fontSize(11.px)
                    if (isDevelopmentMode) fontWeight("700")
                    property("z-index", "9650")
                    property("pointer-events", "none")
                }
            }) {
                Text(dashboardVersionText)
            }
        }

        if (!contentOnlyMode) {
            Div({
                style {
                    position(Position.Fixed)
                    left(contentLeftOffset)
                    right(ContentOffset.RIGHT)
                    top(10.px)
                    height(50.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    minWidth(0.px)
                    overflow("hidden")
                    property("z-index", "9650")
                    property("pointer-events", "none")
                }
            }) {
                val showScreenName = activeContentLabel.isNotBlank() && activeContentLabel != "Home"
                Img(LogoSettings.desktopDashboardSidebarLogo) {
                    style {
                        position(Position.Absolute)
                        property("right", "calc(50% + 9px)")
                        top(0.px)
                        height(44.px)
                        property("object-fit", "contain")
                        property("pointer-events", "auto")
                        cursor("pointer")
                    }
                    onClick { onHome() }
                }
                if (showScreenName) {
                    Div({
                        style {
                            position(Position.Absolute)
                            property("left", "calc(50% + 9px)")
                            top(0.px)
                            height(50.px)
                            width(320.px)
                            minWidth(0.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.FlexStart)
                            property("overflow", "hidden")
                        }
                    }) {
                        Span({
                            style {
                                color(DashboardTheme.TEXT_LIGHT)
                                fontSize(18.px)
                                fontWeight("700")
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                textAlign("left")
                                minWidth(0.px)
                            }
                        }) { Text(activeContentLabel) }
                    }
                }
            }
        }

        PopupEngineHost(
            topBarScreens = resolvedTopBarScreens + extraScreens,
            openKeys = openTopbarScreens,
            contentInsetLeft = contentLeftOffset.value.toDouble(),
            contentInsetTop = popupTopOffset.value.toDouble(),
            contentInsetRight = ContentOffset.RIGHT.value.toDouble(),
            contentInsetBottom = ContentOffset.BOTTOM.value.toDouble(),
            onClose = { key -> openTopbarScreens = openTopbarScreens.filterNot { it == key } }
        )

        NotepadOverlayLayer(art = art)
        ChatPopupOverlayLayer(node = art)
        CRChatPopupOverlayLayer(node = art)
        KontaktKalenderOverlayLayer(node = art)
        KalenderReminderOverlay(art = art)
        SnapPopupOverlayLayer(node = art)
        SnapverwaltungCreateDialogOverlayLayer()
        LhmTypCreateDialogOverlayLayer()

        Div({
            classes(DashboardStyles.mainArea)
            style {
                left(contentLeftOffset)
                top(contentTopOffset)
                bottom(contentBottomOffset)
                display(if (layoutPreferencesReady) DisplayStyle.Flex else DisplayStyle.None)
            }
        }) {
            Div({ classes(DashboardStyles.mainAreaScrollHost) }) {
                retainedContentKeys.forEach { retainedKey ->
                    allScreens.firstOrNull { it.key == retainedKey }?.let { screen ->
                        val isActive = retainedKey == selectedKey
                        val instanceKey = if (isActive) selectedInstanceKey else retainedKey
                        key(instanceKey) {
                            Div({
                                classes(DashboardStyles.mainAreaScreenSlot)
                                style {
                                    display(if (isActive) DisplayStyle.Flex else DisplayStyle.None)
                                }
                            }) {
                                TrackedScreenHost(screen) {
                                    CompositionLocalProvider(LocalScreenInternalTitleVisible provides false) {
                                        screen.content?.invoke(screen)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (!contentOnlyMode) {
            DashboardQuickbar(
                visiblePinnedScreens = visiblePinnedScreens,
                selectedKey = selectedKey,
                allScreens = allScreens,
                quickNavOffset = quickNavOffset,
                quickNavMaxOffset = quickNavMaxOffset,
                contentLeftOffsetPx = contentLeftOffset.value.toDouble(),
                leftReservePx = DASHBOARD_FOOTER_LEFT_RESERVE,
                rightReservePx = DASHBOARD_FOOTER_RIGHT_RESERVE,
                onQuickNavLeft = { quickNavOffset = (quickNavOffset - 1).coerceAtLeast(0) },
                onQuickNavRight = { quickNavOffset = (quickNavOffset + 1).coerceAtMost(quickNavMaxOffset) },
                onSelect = onSelect,
                onUnpin = { key ->
                    pinnedScreens = pinnedScreens.filterNot { it == key }
                    if (key == selectedKey) {
                        onHome()
                    }
                }
            )
        }

        EngineSmartRingHost(
            availableScreens = allScreens,
            featureEnabled = allScreens.any { it.key == ENGINE_SMARTRING_SCREEN_KEY },
            onSlotSelect = ::openNavigationTarget
        )
    }
}

object DashboardStyles : StyleSheet() {
    val appRoot by style {
        width(100.vw)
        height(100.vh)
        overflow("visible")
        backgroundColor(Color(DashboardTheme.DASHBOARD_BG))
        property("background", DashboardTheme.DASHBOARD_BG)
    }
    val sidebarWrapper by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        bottom(0.px)
        width(DashboardTheme.SIDEBAR_WIDTH_FULL)
        property("z-index", DashboardTheme.NAVIGATION_LAYER_Z_INDEX.toString())
        property("transition", "left 0.22s ease")
    }
    val sidebar by style {
        height(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        overflow("hidden")
        property("background", DashboardTheme.SIDEBAR_BG)
        property("border-right", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        property("box-shadow", "0 0 30px ${DashboardTheme.SURFACE_SHADOW}")
    }
    val navHeader by style {
        padding(30.px, 10.px, 20.px, 10.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
    }
    val navItem by style {
        property("box-sizing", "border-box")
        width(100.percent)
        padding(10.px, 16.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        gap(14.px)
        justifyContent(JustifyContent.FlexStart)
        cursor("pointer")
        property("transition", "all 0.2s ease")
        property("border-left", "4px solid transparent")
        color(DashboardTheme.TEXT_MUTED)
    }
    val navIcon by style { width(28.px); height(28.px) }
    val navLabel by style {
        fontSize(14.px)
        color(DashboardTheme.TEXT_LIGHT)
        textAlign("left")
        marginTop(0.px)
    }
    val navLabelMuted by style { opacity(0.8) }
    val navItemStatic by style { cursor("default") }
    val navScroll by style {
        flex(1)
        minHeight(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Stretch)
    }
    val navScrollContent by style {
        flex("0 1 auto")
        minHeight(0.px)
        maxHeight(100.percent)
        overflowY("auto")
        property("overflow-x", "hidden")
        property("scrollbar-width", "none")
        property("-ms-overflow-style", "none")
        padding(8.px, 0.px)
        self + "::-webkit-scrollbar" style { display(DisplayStyle.None) }
    }
    val sidebarBottomSection by style {
        flexShrink(0)
        paddingTop(6.px)
        paddingBottom(10.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
    }
    val sidebarCollapseRow by style {
        flexShrink(0)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        alignItems(AlignItems.Center)
        padding(10.px, 12.px, 4.px, 12.px)
    }
    val sidebarCollapseRowCollapsed by style {
        justifyContent(JustifyContent.Center)
        padding(10.px, 0.px, 4.px, 0.px)
    }
    val sidebarCollapseIcon by style {
        width(18.px)
        height(18.px)
        property("filter", DashboardTheme.ICON_FILTER_WHITE)
    }
    val sidebarPopupDivider by style {
        flexShrink(0)
        height(1.px)
        width(70.percent)
        alignSelf(AlignSelf.Center)
        backgroundColor(DashboardTheme.BORDER_LIGHT)
        margin(10.px, 0.px)
    }
    val sidebarPopupSection by style {
        flexShrink(0)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(4, 36px)")
        justifyContent(JustifyContent.Center)
        gap(8.px)
        padding(12.px, 10.px, 2.px, 10.px)
    }
    val sidebarPopupSectionCollapsed by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        padding(12.px, 0.px, 2.px, 0.px)
    }
    val sidebarPopupIconButton by style {
        width(36.px)
        height(36.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        cursor("pointer")
        property("transition", "background 0.18s ease")
        hover {
            backgroundColor(Color("rgba(255,255,255,0.06)"))
        }
        self + ":hover img" style {
            property("filter", DashboardTheme.ICON_FILTER_ACTIVE_GLOW)
        }
        self + ":hover span" style {
            color(DashboardTheme.ACCENT)
            fontWeight("700")
        }
    }
    val sidebarPopupIconButtonActive by style {
        desc(self, selector("img")) style {
            property("filter", DashboardTheme.ICON_FILTER_ACTIVE_GLOW)
        }
    }
    val sidebarPopupIcon by style {
        width(25.px)
        height(25.px)
        property("filter", DashboardTheme.ICON_FILTER_WHITE)
    }
    val sidebarIdentityBlock by style {
        flexShrink(0)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
        padding(14.px, 0.px, 18.px, 0.px)
        property("border-top", "1px solid ${DashboardTheme.BORDER_LIGHT}")
    }
    val sidebarIdentityRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(14.px)
        padding(8.px, 14.px, 8.px, 20.px)
        minWidth(0.px)
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(13.px)
        fontWeight("600")
        property("transition", "background 0.18s ease")
        hover {
            backgroundColor(Color("rgba(255,255,255,0.06)"))
        }
        self + ":hover img" style {
            property("filter", DashboardTheme.ICON_FILTER_ACTIVE_GLOW)
        }
        self + ":hover span" style {
            color(DashboardTheme.ACCENT)
            fontWeight("700")
        }
    }
    val sidebarIdentityRowCollapsed by style {
        justifyContent(JustifyContent.Center)
        padding(10.px, 0.px)
        gap(0.px)
    }
    val sidebarIdentityIcon by style {
        width(25.px)
        height(25.px)
        flexShrink(0)
        property("filter", DashboardTheme.ICON_FILTER_WHITE)
    }
    val sidebarIdentityText by style {
        minWidth(0.px)
        property("white-space", "nowrap")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
    }
    val sidebarPopupGrid by style {
        position(Position.Fixed)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(4, 36px)")
        justifyContent(JustifyContent.Center)
        gap(8.px)
        padding(10.px)
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        property("border-radius", "8px")
        property("box-shadow", "0 10px 24px ${DashboardTheme.SURFACE_SHADOW}")
        property("z-index", DashboardTheme.NAVIGATION_POPUP_Z_INDEX.toString())
    }
    val sidebarPopupGridItem by style {
        width(36.px)
        height(36.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        cursor("pointer")
        property("transition", "background 0.18s ease")
        hover {
            backgroundColor(Color("rgba(255,255,255,0.06)"))
        }
        self + ":hover img" style {
            property("filter", DashboardTheme.ICON_FILTER_ACTIVE_GLOW)
        }
        self + ":hover span" style {
            color(DashboardTheme.ACCENT)
            fontWeight("700")
        }
    }
    val sidebarTextHoverPopup by style {
        position(Position.Fixed)
        padding(10.px, 16.px)
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        property("border-radius", "8px")
        property("box-shadow", "0 10px 24px ${DashboardTheme.SURFACE_SHADOW}")
        property("z-index", DashboardTheme.NAVIGATION_POPUP_Z_INDEX.toString())
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(13.px)
        fontWeight("600")
        property("white-space", "nowrap")
        property("box-sizing", "border-box")
        hover {
            backgroundColor(Color("rgba(255,255,255,0.06)"))
        }
    }
    val userActionsAnchor by style {
        position(Position.Relative)
        marginLeft(20.px)
        marginBottom(4.px)
        property("width", "fit-content")
    }
    val userActionsRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
    }
    val userLabel by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(14.px)
        padding(8.px, 0.px)
        cursor("pointer")
    }
    val userLabelIcon by style {
        width(28.px)
        height(28.px)
        display(DisplayStyle.Block)
    }
    val userMenuToggle by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        width(36.px)
        height(36.px)
        cursor("pointer")
    }
    val userActionsPopup by style {
        position(Position.Absolute)
        left(0.px)
        bottom(100.percent)
        marginBottom(8.px)
        padding(8.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        gap(12.px)
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        property("border-radius", "12px")
        property("box-shadow", "0 10px 24px ${DashboardTheme.SURFACE_SHADOW}")
        property("z-index", "20")
    }
    val userSettingsOverlay by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        backgroundColor(Color("rgba(8, 10, 14, 0.58)"))
        property("backdrop-filter", "blur(10px)")
        property("z-index", "12000")
    }
    val userSettingsCard by style {
        width(420.px)
        maxWidth(92.vw)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
        padding(18.px)
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        property("border-radius", "18px")
        property("box-shadow", "0 18px 54px ${DashboardTheme.SURFACE_SHADOW}")
    }
    val userSettingsTitleRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        marginBottom(4.px)
    }
    val userSettingsTitle by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(18.px)
        fontWeight("700")
    }
    val userSettingsSubtitle by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(11.px)
        fontWeight("700")
        property("letter-spacing", "0.08em")
        property("text-transform", "uppercase")
        marginTop(4.px)
    }
    val userSettingsDivider by style {
        height(1.px)
        width(100.percent)
        backgroundColor(DashboardTheme.BORDER_LIGHT)
        margin(8.px, 0.px)
    }
    val userSettingsRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        gap(14.px)
        padding(10.px, 12.px)
        borderRadius(12.px)
        cursor("pointer")
        property("transition", "background 0.18s ease")
        hover {
            backgroundColor(Color("rgba(255,255,255,0.06)"))
        }
    }
    val userSettingsLabel by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(14.px)
        fontWeight("600")
    }
    val userActionIconButton by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        width(32.px)
        height(32.px)
        padding(0.px)
        cursor("pointer")
        property("transition", "all 0.2s ease")
        property("box-sizing", "border-box")
    }
    val userActionIcon by style {
        width(32.px)
        height(32.px)
        display(DisplayStyle.Block)
    }
    val divider by style {
        height(1.px)
        width(70.percent)
        alignSelf(AlignSelf.Center)
        property("margin", "6px 0 18px 0")
        backgroundColor(DashboardTheme.BORDER_LIGHT)
    }
    val mainArea by style {
        position(Position.Fixed)
        left(ContentOffset.LEFT)
        right(ContentOffset.RIGHT)
        top(ContentOffset.TOP)
        bottom(ContentOffset.BOTTOM)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        minWidth(0.px)
        minHeight(0.px)
        overflow("hidden")
        property("box-sizing", "border-box")
        property("z-index", "1")
        property("transition", "left 0.22s ease, top 0.22s ease")
    }
    val mainAreaScrollHost by style {
        width(100.percent)
        height(100.percent)
        minWidth(0.px)
        minHeight(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        overflow("auto")
        property("box-sizing", "border-box")
        property("scrollbar-width", "thin")
        property("scrollbar-color", "rgba(56, 62, 74, 0.98) transparent")
        self + "::-webkit-scrollbar" style {
            width(12.px)
            height(12.px)
            backgroundColor(Color.transparent)
        }
        self + "::-webkit-scrollbar-track" style {
            backgroundColor(Color.transparent)
        }
        self + "::-webkit-scrollbar-thumb" style {
            borderRadius(999.px)
            property("background", "linear-gradient(180deg, rgba(72, 79, 92, 0.98) 0%, rgba(46, 52, 62, 0.98) 100%)")
            property("border", "1px solid rgba(255,255,255,0.10)")
        }
        self + "::-webkit-scrollbar-corner" style {
            backgroundColor(Color.transparent)
        }
    }
    val mainAreaScreenSlot by style {
        width(100.percent)
        height(100.percent)
        minWidth(0.px)
        minHeight(0.px)
        flexDirection(FlexDirection.Column)
    }
    val quickBar by style {
        position(Position.Fixed)
        left(ContentOffset.LEFT)
        right(ContentOffset.RIGHT)
        bottom(0.px)
        height(DashboardTheme.BOTTOM_BAR_HEIGHT)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        property("z-index", "2")
    }
    val quickPager by style {
        position(Position.Relative)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        maxWidth(96.vw)
        width(96.vw)
        minWidth(0.px)
    }
    val quickItemsViewport by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        width(100.percent)
        minWidth(0.px)
        overflow("hidden")
    }
    val quickItems by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        minWidth(0.px)
    }
    val quickItem by style {
        height(100.percent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        padding(0.px, 22.px)
        fontSize(14.px)
        cursor("pointer")
        minWidth(0.px)
    }
    val quickDivider by style { width(1.px); height(18.px); backgroundColor(DashboardTheme.BORDER_LIGHT) }
    val quickNavArrow by style {
        position(Position.Absolute)
        top(50.percent)
        property("transform", "translateY(-50%)")
        width(26.px)
        height(26.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        cursor("pointer")
        flexShrink(0)
        property("z-index", "1")
    }
    val quickNavArrowLeft by style { left(0.px) }
    val quickNavArrowRight by style { right(0.px) }
    val quickNavArrowDisabled by style {
        opacity(0.35)
        cursor("default")
    }
    val quickNavArrowIcon by style {
        width(18.px)
        height(18.px)
        property("filter", DashboardTheme.ICON_FILTER_WHITE)
    }
    val categoryRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(14.px)
        padding(8.px, 14.px, 8.px, 20.px)
        marginTop(22.px)
        cursor("pointer")
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(12.px)
        fontWeight("600")
        property("letter-spacing", "0.03em")
        property("transition", "background 0.18s ease")
        hover {
            backgroundColor(Color("rgba(255,255,255,0.06)"))
        }
        self + ":hover img" style {
            property("filter", DashboardTheme.ICON_FILTER_ACTIVE_GLOW)
        }
    }
    val categoryRowCollapsed by style {
        justifyContent(JustifyContent.Center)
        padding(8.px, 0.px)
        marginTop(16.px)
        gap(0.px)
        width(100.percent)
        property("box-sizing", "border-box")
    }
    val categoryIcon by style {
        width(25.px)
        height(25.px)
        property("filter", DashboardTheme.ICON_FILTER_WHITE)
    }
    val categoryText by style {
        fontSize(14.px)
        fontWeight("600")
    }
    val screenRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(14.px)
        padding(8.px, 16.px, 8.px, 20.px)
        cursor("pointer")
        color(DashboardTheme.TEXT_MUTED)
        fontSize(12.px)
        property("transition", "background 0.2s ease, color 0.2s ease")
        hover {
            backgroundColor(Color("rgba(255,255,255,0.06)"))
        }
        self + ":hover img" style {
            property("filter", DashboardTheme.ICON_FILTER_ACTIVE_GLOW)
        }
        self + ":hover span" style {
            color(DashboardTheme.ACCENT)
            fontWeight("700")
        }
    }
    val screenRowCollapsed by style {
        justifyContent(JustifyContent.Center)
        padding(10.px, 0.px)
        gap(0.px)
        width(100.percent)
        property("box-sizing", "border-box")
    }
    val stepIcon by style {
        width(25.px)
        height(25.px)
        marginRight(10.px)
        property("filter", DashboardTheme.ICON_FILTER_WHITE)
    }
    val screenActiveRow by style {
        property("border-left", "4px solid ${DashboardTheme.ACCENT}")
        property("background", "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.10) 100%)")
        property("background-clip", "padding-box")
        property("box-shadow", "inset 0 0 22px rgba(255, 255, 255, 0.12), 0 0 18px rgba(255, 255, 255, 0.10)")
        desc(self, selector("span")) style {
            color(DashboardTheme.ACCENT)
            fontWeight("700")
        }
        desc(self, selector("img")) style {
            property("filter", DashboardTheme.ICON_FILTER_ACTIVE_GLOW)
        }
    }
    val sidebarHoverPopup by style {
        position(Position.Fixed)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        padding(8.px, 0.px)
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        property("border-radius", "8px")
        property("box-shadow", "0 10px 24px ${DashboardTheme.SURFACE_SHADOW}")
        property("z-index", DashboardTheme.NAVIGATION_POPUP_Z_INDEX.toString())
    }
    val sidebarHoverPopupTitle by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(13.px)
        fontWeight("700")
        padding(8.px, 16.px, 10.px, 16.px)
        textAlign("center")
    }
    val topbarHotzone by style {
        position(Position.Fixed)
        top(0.px)
        left(0.px)
        right(0.px)
        height(14.px)
        property("z-index", "9400")
        backgroundColor(Color.transparent)
    }
    val sidebarHotzone by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        bottom(ContentOffset.BOTTOM)
        width(14.px)
        property("z-index", "9400")
        backgroundColor(Color.transparent)
    }
    val topbarTitleGroup by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(14.px)
        width(100.percent)
        minWidth(0.px)
        property("pointer-events", "none")
    }
    val topbarTitleDivider by style {
        height(1.px)
        flex(1)
        backgroundColor(DashboardTheme.BORDER_LIGHT)
    }
    val topbarTitleStack by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        minWidth(0.px)
        maxWidth(100.percent)
    }
    val topbarCompanyText by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(11.px)
        fontWeight("600")
        textAlign("center")
        property("white-space", "nowrap")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        width(100.percent)
    }
    val topbarTitleText by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(20.px)
        fontWeight("700")
        textAlign("center")
        property("white-space", "nowrap")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
    }
}
